/* Joi: cart engine + overlay manager.
   - AJAX Cart API (/cart/add.js, /cart/change.js) with Section Rendering so
     the drawer/cart page are always re-rendered by Liquid (receipt styling,
     free-shipping meter, discounts) — no client-side templating drift.
   - Every add opens the drawer (no toast), matching the prototype.
   - Overlays (cart z-60, menu z-65, search z-70) are mutually exclusive,
     close on scrim/✕/Esc, lock body scroll, restore focus. Hidden overlays
     are display:none + inert — nothing invisible covers the page.
   - Joi fix 9-8: closing now mirrors opening — .is-closing plays the exit
     animation (drawer slides back out, scrim fades; CSS in joi-fixes.css)
     and the overlay is hidden after it finishes. Instant on reduced motion
     and when switching between overlays. */

const config = JSON.parse(document.getElementById('joi-config')?.textContent || '{}');

/* ---------------- Overlay manager ---------------- */

const overlays = new Map();
let openOverlay = null;
let lastTrigger = null;
const CLOSE_MS = 340;
const reduceMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

function registerOverlays(root = document) {
  root.querySelectorAll('.joi-overlay-root[data-overlay]').forEach((el) => {
    overlays.set(el.dataset.overlay, el);
  });
}

function open(name, trigger) {
  const el = overlays.get(name);
  if (!el) return;
  if (openOverlay && openOverlay !== name) close(true);
  lastTrigger = trigger || document.activeElement;
  /* Joi fix 9-8: clear any leftover close state / suppressed animation so
     the slide-in replays cleanly on every open */
  el.classList.remove('is-closing');
  el.querySelectorAll('.joi-drawer, .joi-search-overlay').forEach((d) => { d.style.animation = ''; });
  el.hidden = false;
  el.removeAttribute('inert');
  document.body.classList.add('joi-scroll-lock');
  openOverlay = name;
  const focusable = el.querySelector('[data-overlay-autofocus]') ||
    el.querySelector('input, button, a[href]');
  if (focusable) focusable.focus({ preventScroll: true });
  document.dispatchEvent(new CustomEvent('joi:overlay:opened', { detail: { name } }));
}

function close(immediate = false) {
  if (!openOverlay) return;
  const el = overlays.get(openOverlay);
  openOverlay = null;
  document.body.classList.remove('joi-scroll-lock');
  if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
  lastTrigger = null;
  if (!el) return;
  const finish = () => {
    el.hidden = true;
    el.setAttribute('inert', '');
    el.classList.remove('is-closing');
  };
  if (immediate || reduceMotionMq.matches) {
    finish();
    return;
  }
  /* Joi fix 9-8: mirror-image exit — clear any inline animation:none left by
     swapSections so the close keyframes actually run, then hide after ~340ms */
  el.querySelectorAll('.joi-drawer, .joi-search-overlay').forEach((d) => { d.style.animation = ''; });
  el.classList.add('is-closing');
  window.setTimeout(finish, CLOSE_MS);
}

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-overlay-open]');
  if (opener) {
    e.preventDefault();
    open(opener.dataset.overlayOpen, opener);
    return;
  }
  if (e.target.closest('[data-overlay-close]')) {
    e.preventDefault();
    close();
  }
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') close();
});

window.JoiOverlays = { open, close };

/* ---------------- Cart state ---------------- */

const DRAWER_FALLBACK = 'cart-drawer';

/* Joi fix: the drawer lives in the overlay section GROUP, so its rendered
   section id is "sections--<n>__cart-drawer", not "cart-drawer". Resolve it
   from the DOM — hard-coding the bare name made every Section Rendering swap
   a silent no-op (qty +/− looked dead, second adds never refreshed). */
function drawerSectionId() {
  const overlay = document.querySelector('[data-overlay="cart"]');
  const wrapper = overlay ? overlay.closest('[id^="shopify-section-"]') : null;
  return wrapper ? wrapper.id.replace('shopify-section-', '') : DRAWER_FALLBACK;
}

function cartCountEls() { return document.querySelectorAll('[data-cart-count]'); }

function setCount(count) {
  /* Joi fix: blank at zero — the tote shows a number only when it holds something */
  const value = Number(count) > 0 ? String(count) : '';
  cartCountEls().forEach((el) => { el.textContent = value; });
}

function sectionsToRequest() {
  const ids = [drawerSectionId()];
  document.querySelectorAll('[data-joi-cart-section]').forEach((el) => {
    if (el.dataset.joiCartSection) ids.push(el.dataset.joiCartSection);
  });
  return ids.join(',');
}

function swapSections(sections) {
  if (!sections) return;
  const keepOpen = openOverlay;
  Object.entries(sections).forEach(([id, html]) => {
    if (!html) return;
    const target = document.getElementById(`shopify-section-${id}`);
    if (!target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.getElementById(`shopify-section-${id}`) || parsed.body;
    target.innerHTML = fresh.innerHTML;
  });
  registerOverlays();
  /* Joi fix: re-rendered overlay markup ships hidden+inert — if the drawer was
     open (qty change, second add), keep it open and skip replaying the
     slide-in animation so the panel doesn't flicker on every change */
  if (keepOpen) {
    const el = overlays.get(keepOpen);
    if (el) {
      el.hidden = false;
      el.removeAttribute('inert');
      el.querySelectorAll('.joi-drawer').forEach((d) => { d.style.animation = 'none'; });
    }
  }
  document.dispatchEvent(new CustomEvent('joi:rerendered'));
}

async function refreshCount() {
  try {
    const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
    setCount(cart.item_count);
  } catch (e) { /* non-fatal */ }
}

function busy(el, on) {
  if (!el) return;
  el.toggleAttribute('aria-busy', on);
  el.toggleAttribute('disabled', on);
}

async function addToCart(body, trigger) {
  busy(trigger, true);
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionsToRequest(), sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok) {
      window.JoiToast?.show(data.description || data.message || config.strings?.cartError);
      return false;
    }
    swapSections(data.sections);
    await refreshCount();
    if (config.cartType === 'page') {
      window.location.href = '/cart';
    } else {
      open('cart', trigger);
    }
    return true;
  } catch (err) {
    window.JoiToast?.show(config.strings?.cartError);
    return false;
  } finally {
    busy(trigger, false);
  }
}

async function changeLine(key, quantity) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, sections: sectionsToRequest(), sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok) {
      window.JoiToast?.show(data.description || data.message || config.strings?.cartError);
      return;
    }
    swapSections(data.sections);
    setCount(data.item_count);
  } catch (err) {
    window.JoiToast?.show(config.strings?.cartError);
  }
}

/* Joi: discount codes apply via the storefront /discount route, then the cart
   sections re-render. Some code types only validate at checkout — the drawer
   shows a neutral "saved" note in that case rather than pretending. */
async function applyDiscount(code, form) {
  if (!code) return;
  busy(form.querySelector('button'), true);
  try {
    await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET' });
    const res = await fetch(`${window.location.pathname}?sections=${sectionsToRequest()}`);
    const sections = await res.json();
    swapSections(sections);
    const applied = document.querySelector('[data-joi-discount-applied]');
    if (!applied) {
      const note = document.querySelector('[data-joi-discount-note]');
      if (note) note.hidden = false;
    }
  } catch (err) {
    window.JoiToast?.show(config.strings?.cartError);
  } finally {
    busy(document.querySelector('[data-joi-discount-form] button'), false);
  }
}

/* Joi fix 24-7: CHECKOUT navigates straight to checkout. The drawer/cart-page
   forms POST to /cart natively as a no-JS fallback, but that route is a
   silent no-op inside admin/editor preview frames — so with JS we go to
   /checkout directly (same path BUY NOW uses), persisting the cart note
   first when one is present. */
function goToCheckout(form) {
  const root = window.Shopify?.routes?.root || '/';
  const go = () => { window.location.href = `${root}checkout`; };
  const noteEl = form.querySelector('[name="note"]') ||
    (form.id ? document.querySelector(`textarea[form="${form.id}"]`) : null);
  if (noteEl) {
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: noteEl.value }),
    }).then(go, go);
  } else {
    go();
  }
}

/* ---------------- Event delegation ---------------- */

document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-joi-atc-form]');
  if (form) {
    e.preventDefault();
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    const quantity = Number(fd.get('quantity') || 1);
    const properties = {};
    fd.forEach((v, k) => {
      const m = k.match(/^properties\[(.+)\]$/);
      if (m) properties[m[1]] = v;
    });
    addToCart({ id, quantity, ...(Object.keys(properties).length ? { properties } : {}) }, form.querySelector('[type="submit"]'));
    return;
  }
  const discountForm = e.target.closest('[data-joi-discount-form]');
  if (discountForm) {
    e.preventDefault();
    applyDiscount(discountForm.querySelector('input')?.value.trim(), discountForm);
    return;
  }
  /* Joi fix 24-7: any cart form whose submitter carries name="checkout" */
  const cartForm = e.target.closest('form');
  if (cartForm && cartForm.querySelector('[name="checkout"]')) {
    e.preventDefault();
    goToCheckout(cartForm);
  }
});

document.addEventListener('click', (e) => {
  const quick = e.target.closest('[data-joi-quick-add]');
  if (quick) {
    e.preventDefault();
    addToCart({ id: Number(quick.dataset.variantId), quantity: 1 }, quick);
    return;
  }
  const step = e.target.closest('[data-joi-line-step]');
  if (step) {
    e.preventDefault();
    const line = step.closest('[data-line-key]');
    const input = line?.querySelector('[data-joi-line-qty]');
    if (!line || !input) return;
    const next = Math.max(0, Number(input.value || 0) + Number(step.dataset.joiLineStep));
    changeLine(line.dataset.lineKey, next);
    return;
  }
  const remove = e.target.closest('[data-joi-line-remove]');
  if (remove) {
    e.preventDefault();
    const line = remove.closest('[data-line-key]');
    if (line) changeLine(line.dataset.lineKey, 0);
  }
});

document.addEventListener('change', (e) => {
  const input = e.target.closest('[data-joi-line-qty]');
  if (input) {
    const line = input.closest('[data-line-key]');
    if (line) changeLine(line.dataset.lineKey, Math.max(0, Number(input.value || 0)));
  }
});

registerOverlays();
document.addEventListener('shopify:section:load', () => registerOverlays());

window.JoiCart = { addToCart, changeLine, refreshCount, goToCheckout };
